import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Crown, Medal, Trophy } from "lucide-react";
import { getBracketLeaderboard } from "@/lib/bracket.functions";
import { useAuth } from "@/hooks/useAuth";
import { trackEvent } from "@/lib/audit";

/**
 * TOP 3 del partido — no new engine/polling/realtime.
 * Consumes the already-cached ["bracket","lb"] query.
 * Shows "Tu posición #N de X" when the user is NOT in the top 3.
 */
export function MatchTop3Surface() {
  const { user } = useAuth();
  const q = useQuery({
    queryKey: ["bracket", "lb"],
    queryFn: () => getBracketLeaderboard({ data: { limit: 200 } }),
    staleTime: 60_000,
  });
  const rows = q.data?.rows ?? [];
  const total = rows.length;
  const top3 = rows.slice(0, 3);
  const myRank = useMemo(() => {
    if (!user || total === 0) return null;
    const i = rows.findIndex((r) => r.user_id === user.id);
    return i >= 0 ? i + 1 : null;
  }, [rows, user, total]);
  const inTop3 = myRank !== null && myRank <= 3;

  useEffect(() => {
    if (rows.length > 0) void trackEvent("post_match_top3_viewed", { total, my_rank: myRank });
  }, [rows.length, total, myRank]);

  if (q.isLoading) {
    return (
      <div className="rounded-xl border border-border/40 p-3 text-[11px] text-muted-foreground">
        Calculando TOP 3…
      </div>
    );
  }
  if (top3.length === 0) return null;

  const icons = [
    <Crown key="i0" className="h-3.5 w-3.5 text-amber-400" />,
    <Medal key="i1" className="h-3.5 w-3.5 text-slate-300" />,
    <Trophy key="i2" className="h-3.5 w-3.5 text-amber-700" />,
  ];

  return (
    <div className="rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-transparent p-3">
      <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-amber-500">
        <Crown className="h-3 w-3" /> Top 3 del partido
      </div>
      <ul className="mt-2 space-y-1">
        {top3.map((r, i) => {
          const mine = user && r.user_id === user.id;
          return (
            <li
              key={`${r.user_id ?? "anon"}-${i}`}
              className={`flex items-center justify-between rounded-md px-2 py-1 text-[11px] ${
                mine ? "bg-amber-500/20 ring-1 ring-amber-500/50" : "bg-background/40"
              }`}
            >
              <span className="flex items-center gap-1.5 font-bold">
                {icons[i]}
                <span className="tabular-nums">#{i + 1}</span>
                <span className="text-muted-foreground">
                  {(r.user_id ?? "anon").slice(0, 8)}
                </span>
                {mine && <span className="text-amber-500">(Tú)</span>}
              </span>
              <span className="font-mono tabular-nums font-black">
                {r.total_points ?? 0} pts · {r.exact_count ?? 0} 🎯
              </span>
            </li>
          );
        })}
      </ul>
      {!inTop3 && (
        <div className="mt-2 flex items-center justify-between rounded-md bg-background/40 px-2 py-1 text-[11px]">
          <span className="font-bold">
            Tu posición:{" "}
            <span className="font-black tabular-nums text-amber-500">
              {myRank ? `#${myRank}` : "—"}
            </span>
            <span className="text-muted-foreground"> de {total}</span>
          </span>
          <span className="text-[10px] text-muted-foreground">Tú</span>
        </div>
      )}
    </div>
  );
}